<script setup lang="ts">
import UiSection from '@/components/UiSection/UiSection.vue'
import UiHeader from '@/components/UiHeader/UiHeader.vue'
import DataViewTable from '@/domains/investor/components/DataView/DataViewTable.vue'
import type { IDataViewProps } from './DataView.types'
import DataViewFilters from '@/domains/investor/components/DataView/DataViewFilters/DataViewFilters.vue'
import DataViewEmptyState from '@/domains/investor/components/DataView/DataViewEmptyState/DataViewEmptyState.vue'

const props = withDefaults(defineProps<IDataViewProps>(), {
  itemsPerPage: 20,
  showPagination: false,
  isLoading: false,
  sectionId: 'dataViewSection',
  sectionColor: 'white',
})

const page = defineModel<number>('page', { default: 1 })

const externalModels = computed(() => props.tableFilters?.models)

const localCategory = ref<string | number | null>(null)
const localYear = ref<number[]>([])
const localMonths = ref<number[]>([])
const localSearchValue = ref('')

const categoryModel = computed<string | number | null>({
  get: () => (externalModels.value?.['secondCategory']?.value as string | number | null | undefined) ?? localCategory.value,
  set: (value: string | number | null) => {
    if (externalModels.value?.['secondCategory']) {
      externalModels.value['secondCategory'].value = value
      return
    }
    localCategory.value = value
  },
})

const yearModel = computed<number[]>({
  get: () => (externalModels.value?.['year']?.value as number[]) ?? localYear.value,
  set: (value: number[]) => {
    if (externalModels.value?.['year']) {
      externalModels.value['year'].value = value
      return
    }
    localYear.value = value
  },
})

const monthsModel = computed<number[]>({
  get: () => (externalModels.value?.['months']?.value as number[]) ?? localMonths.value,
  set: (value: number[]) => {
    if (externalModels.value?.['months']) {
      externalModels.value['months'].value = value
      return
    }
    localMonths.value = value
  },
})

const searchValueModel = computed<string>({
  get: () => (externalModels.value?.['searchDraft']?.value as string | undefined) ?? localSearchValue.value,
  set: (value: string) => {
    if (externalModels.value?.['searchDraft']) {
      externalModels.value['searchDraft'].value = value
      return
    }
    localSearchValue.value = value
  },
})

const showSearch = computed(() => props.tableFilters?.showSearch ?? false)
const showMonths = computed(() => props.tableFilters?.showMonths ?? false)
const showCategory = computed(() => props.tableFilters?.showCategory ?? true)
const showYear = computed(() => props.tableFilters?.showYear ?? true)
const tableItems = computed(() => props.tableItems ?? [])
const filtersTotal = computed(() => props.total ?? tableItems.value.length)
const filterOptions = computed(() => props.tableFilters?.filterOptions)

const hasActiveFilters = computed(() => {
  return props.tableFilters?.hasActiveFilters ?? false
})

const onSearch = (value: string) => {
  page.value = 1
  props.tableFilters?.onSearch?.(value)
}

const onClear = () => {
  page.value = 1
  props.tableFilters?.clearFilters?.()
}

const hasItems = computed(() => tableItems.value.length > 0)

const { t } = useI18n()

const headerConfig = computed(() => {
  const config = props.header ?? {
    text: 'common.dataView.heading',
    size: 'h5',
    position: 'center',
  }
  return {
    ...config,
    text: t(config.text),
  }
})
</script>

<template>
  <div class="dataView">
    <UiSection
      :id="props.sectionId"
      class="dataView__section"
      :color="props.sectionColor"
    >
      <UiHeader
        v-if="headerConfig.text"
        :header="headerConfig.text"
        :position="headerConfig.position"
        :header-size="headerConfig.size"
      />

      <div class="section__container">
        <div class="section__row">
          <div class="section__col">
            <DataViewFilters
              v-if="props.tableFilters"
              v-model:category="categoryModel"
              v-model:year="yearModel"
              v-model:months="monthsModel"
              v-model:search-value="searchValueModel"
              :total="filtersTotal"
              :show-search="showSearch"
              :show-months="showMonths"
              :show-category="showCategory"
              :show-year="showYear"
              :filter-options="filterOptions"
              :has-active-filters="hasActiveFilters"
              :is-loading="isLoading"
              :labels="props.tableFilters?.labels"
              @search="onSearch"
              @clear="onClear"
            />
          </div>
        </div>

        <div class="section__row">
          <div class="section__col">
            <Transition
              name="fade"
              mode="out-in"
            >
              <div
                v-if="hasItems"
                class="dataView__results"
                :class="['dataView__results',{'dataView__results--loading': isLoading}]"
              >
                <Transition
                  name="fade"
                  mode="out-in"
                >
                  <DataViewTable
                    :key="page"
                    :items="tableItems"
                    :columns="tableColumns"
                  />
                </Transition>

                <UiPagination
                  v-if="showPagination"
                  v-model="page"
                  :total="filtersTotal"
                  :items-per-page="itemsPerPage"
                  class="dataView__pagination"
                />
              </div>

              <DataViewEmptyState
                v-else
                :text="emptyStateMessage"
                class="dataViewView__empty"
              />
            </Transition>
          </div>
        </div>
      </div>
    </UiSection>
  </div>
</template>

<style lang="scss" scoped>
.dataView {
  :deep(.ui-header) {
    margin-bottom: 6.4rem;
  }

  &__results {
    display: flex;
    flex-direction: column;
    gap: 3.2rem;
    transition: opacity 0.25s ease;

    &--loading {
      opacity: 0.4;
      pointer-events: none;
    }
  }

  &__pagination {
    justify-content: center;
    margin-top: 0.8rem;
  }

  &__empty {
    padding: 4.8rem 0;
    color: $text-200;
    text-align: center;
  }
}

.dataView__section {
  .section & {
    margin: 0;
    padding: 0;
  }
}
</style>
